import React, { useEffect, useRef, useState } from "react";
import {
  useEsgChatMutation,
  useReportIssueMutation,
} from "../../../redux/services/esgnewmodule";
import { Button, Input, message, Modal, Form, Tag } from "antd";
import {
  MessageOutlined,
  CloseOutlined,
  SendOutlined,
  UserOutlined,
  RobotOutlined,
  BugOutlined,
  CameraOutlined,
  SafetyCertificateOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import { motion, AnimatePresence } from "framer-motion";
import { useChatbot } from "../../../providers/ChatbotContext";
import { useLocation } from "react-router-dom";

const { TextArea } = Input;

const Chatbot = () => {
  const { state, dispatch } = useChatbot();
  const {
    conversation,
    isOpen,
    showOptions,
    activeChat,
    messageText,
    typingText,
    isTyping,
    isSending,
  } = state;

  const [reportModalVisible, setReportModalVisible] = useState(false);
  const [pageSnapshot, setPageSnapshot] = useState(null);
  const [screenshotFile, setScreenshotFile] = useState(null);
  const [reportForm] = Form.useForm();

  const location = useLocation();
  const isPocSubmitterRoute = location.pathname === "/esg/poc/submitter";

  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const typingIntervalRef = useRef(null);
  const inputRef = useRef(null);
  const chatbotRef = useRef(null);

  const [esgChat, { isLoading, isError, data }] = useEsgChatMutation();
  const [reportIssue, { isLoading: isReportLoading }] =
    useReportIssueMutation();

  // Action creators
  const setConversation = (payload) =>
    dispatch({ type: "SET_CONVERSATION", payload });
  const addMessage = (payload) => dispatch({ type: "ADD_MESSAGE", payload });
  const setIsOpen = (payload) => dispatch({ type: "SET_IS_OPEN", payload });
  const setShowOptions = (payload) =>
    dispatch({ type: "SET_SHOW_OPTIONS", payload });
  const setActiveChat = (payload) =>
    dispatch({ type: "SET_ACTIVE_CHAT", payload });
  const setMessageText = (payload) =>
    dispatch({ type: "SET_MESSAGE_TEXT", payload });
  const setTypingText = (payload) =>
    dispatch({ type: "SET_TYPING_TEXT", payload });
  const setIsTyping = (payload) => dispatch({ type: "SET_IS_TYPING", payload });
  const setIsSending = (payload) =>
    dispatch({ type: "SET_IS_SENDING", payload });
  const clearTypingIntervalAction = (payload) =>
    dispatch({ type: "CLEAR_TYPING_INTERVAL", payload });

  // Auto-scroll
  useEffect(() => {
    scrollToBottom();
  }, [conversation, typingText]);

  useEffect(() => {
    return () => {
      if (typingIntervalRef.current) {
        clearInterval(typingIntervalRef.current);
        clearTypingIntervalAction(typingIntervalRef.current);
      }
    };
  }, []);

  // Focus input when chat opens
  useEffect(() => {
    if (activeChat && inputRef.current) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 400);
    }
  }, [activeChat]);

  // Outside click close
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!isOpen) return;
      if (chatbotRef.current && chatbotRef.current.contains(event.target)) {
        return;
      }
      setIsOpen(false);
      setActiveChat(false);
      setShowOptions(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Clean markdown
  const cleanMarkdown = (text) => {
    if (!text) return "";
    return text
      .replace(/^#{1,6}\s*/gm, "")
      .replace(/\*\*(.*?)\*\*/g, "$1")
      .replace(/\*(.*?)\*/g, "$1")
      .replace(/_(.*?)_/g, "$1")
      .replace(/\[(.*?)\]\(.*?\)/g, "$1")
      .replace(/`(.*?)`/g, "$1")
      .replace(/\n\s*\n\s*\n/g, "\n\n")
      .trim();
  };

  const startTypingAnimation = (fullText, sources = [], disclaimer = "") => {
    const cleanedText = cleanMarkdown(fullText);
    setIsTyping(true);
    setTypingText("");
    let index = 0;

    if (typingIntervalRef.current) {
      clearInterval(typingIntervalRef.current);
    }

    typingIntervalRef.current = setInterval(() => {
      if (index < cleanedText.length) {
        setTypingText(cleanedText.slice(0, index + 1));
        index++;
      } else {
        clearInterval(typingIntervalRef.current);
        setIsTyping(false);
        addMessage({
          id: Date.now() + 1,
          text: cleanedText,
          isUser: false,
          timestamp: new Date(),
          sources,
          disclaimer,
        });
        setTypingText("");
      }
    }, 15);
  };

  const handleSendMessage = async () => {
    if (!messageText.trim()) {
      message.error("Please enter a message");
      return;
    }

    setIsSending(true);

    const userMessage = {
      id: Date.now(),
      text: messageText,
      isUser: true,
      timestamp: new Date(),
    };

    addMessage(userMessage);
    const currentMessage = messageText;
    setMessageText("");

    try {
      const response = await esgChat({
        message: currentMessage,
        token: "yourAccessTokenHere",
        refreshToken: "yourRefreshTokenHere",
        tenantId: "demo",
      }).unwrap();

      const botResponse =
        response?.message ||
        "I apologize, but I couldn't process your request. Please try again.";

      const sources = response?.sources || [];
      const disclaimer = response?.disclaimer || "";

      startTypingAnimation(botResponse, sources, disclaimer);
    } catch (error) {
      console.error("Error sending message:", error);
      const errorMessage =
        "Sorry, I'm experiencing technical difficulties. Please try again in a moment.";
      startTypingAnimation(errorMessage);
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const base64ToFile = (base64, filename) => {
    const arr = base64.split(",");
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);

    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }

    return new File([u8arr], filename, { type: mime });
  };

  const takePageSnapshot = async () => {
    try {
      setShowOptions(false);
      setIsOpen(false);

      await new Promise((resolve) => setTimeout(resolve, 100));

      const html2canvas = (await import("html2canvas")).default;
      const canvas = await html2canvas(document.body, {
        scale: 0.5,
        useCORS: true,
        allowTaint: true,
        backgroundColor: "#ffffff",
      });

      const screenshot = canvas.toDataURL("image/png");
      setPageSnapshot(screenshot);

      const file = base64ToFile(screenshot, "screenshot.png");
      setScreenshotFile(file);

      setReportModalVisible(true);
      message.success("Page snapshot captured successfully!");
    } catch (error) {
      console.error("Error taking snapshot:", error);
      message.error(
        "Failed to capture page snapshot. You can still report the issue."
      );
      setReportModalVisible(true);
    }
  };

  const handleReportIssue = async (values) => {
    try {
      const formData = new FormData();
      formData.append("description", values.description);

      if (screenshotFile) {
        formData.append("screenshot", screenshotFile);
      }

      await reportIssue(formData).unwrap();

      message.success("Thank you! Your issue has been reported successfully.");
      setReportModalVisible(false);
      reportForm.resetFields();
      setPageSnapshot(null);
      setScreenshotFile(null);
      setActiveChat(false);
      setShowOptions(false);
      setIsOpen(false);
    } catch (error) {
      console.error("Error reporting issue:", error);
      message.error("Failed to submit report. Please try again.");
    }
  };

  const formatMessage = (text) => {
    const parts = text.split("\n");
    return parts.map((line, index) => (
      <span key={index}>
        {line}
        {index < parts.length - 1 && <br />}
      </span>
    ));
  };

  const renderSources = (sources) => {
    if (!sources || sources.length === 0) return null;
    return (
      <div className="sources-section">
        <div className="sources-title">
          <SafetyCertificateOutlined />
          Sources
        </div>
        <div className="sources-list">
          {sources.map((source, index) => (
            <Tag key={index} color="blue" className="source-tag">
              {source}
            </Tag>
          ))}
        </div>
      </div>
    );
  };

  const renderDisclaimer = (disclaimer) => {
    if (!disclaimer) return null;
    return (
      <div className="disclaimer-section">
        <div className="disclaimer-title">
          <InfoCircleOutlined />
          Note
        </div>
        <div className="disclaimer-text">{disclaimer}</div>
      </div>
    );
  };

  const handleMainButtonClick = () => {
    if (!isOpen) {
      setShowOptions(true);
      setIsOpen(true);
    } else {
      if (activeChat) {
        setActiveChat(false);
      }
      if (showOptions) {
        setShowOptions(false);
      }
      setIsOpen(false);
    }
  };

  const handleChatOptionClick = () => {
    setActiveChat(true);
    setShowOptions(false);
  };

  const handleReportOptionClick = () => {
    takePageSnapshot();
    setShowOptions(false);
  };

  // Animations
  const iconVariants = {
    initial: { scale: 1, rotate: 0 },
    hover: {
      scale: 1.1,
      rotate: [0, -5, 5, -5, 0],
      transition: { duration: 0.5 },
    },
    tap: { scale: 0.9 },
  };

  const chatWindowVariants = {
    closed: {
      opacity: 0,
      scale: 0.8,
      y: 20,
      transition: { duration: 0.2 },
    },
    open: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: {
        duration: 0.4,
        type: "spring",
        stiffness: 400,
        damping: 30,
      },
    },
  };

  const optionsVariants = {
    closed: {
      opacity: 0,
      scale: 0.8,
      y: 10,
      transition: { duration: 0.2 },
    },
    open: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: {
        duration: 0.3,
        type: "spring",
        stiffness: 500,
        damping: 30,
      },
    },
  };

  const messageVariants = {
    initial: { opacity: 0, y: 20, scale: 0.8 },
    animate: { opacity: 1, y: 0, scale: 1 },
    exit: { opacity: 0, y: -10, scale: 0.9 },
  };

  const loadingDotsVariants = {
    animate: {
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const dotVariants = {
    animate: {
      y: [0, -8, 0],
      transition: {
        duration: 0.6,
        repeat: Infinity,
        repeatType: "loop",
      },
    },
  };

  return (
    <div className="chatbot-container" ref={chatbotRef}>
      {/* Floating Chat Icon */}
      <motion.div
        className="chatbot-icon"
        variants={iconVariants}
        initial="initial"
        whileHover="hover"
        whileTap="tap"
        onClick={handleMainButtonClick}
        animate={{
          boxShadow: isOpen
            ? "0 8px 30px rgba(102, 126, 234, 0.4)"
            : "0 8px 25px rgba(0, 0, 0, 0.15)",
        }}
      >
        <AnimatePresence mode="wait">
          {isOpen ? (
            <motion.div
              key="close"
              initial={{ rotate: -180, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: 180, opacity: 0 }}
              transition={{ duration: 0.3, type: "spring" }}
            >
              <CloseOutlined style={{ fontSize: "22px", color: "white" }} />
            </motion.div>
          ) : (
            <motion.div
              key="message"
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              <MessageOutlined style={{ fontSize: "22px", color: "white" }} />
            </motion.div>
          )}
        </AnimatePresence>

        {!isOpen && conversation.length === 1 && (
          <motion.div
            className="notification-dot"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 1 }}
          />
        )}
      </motion.div>

      {/* Options Menu (single, with route check) */}
      <AnimatePresence>
        {isOpen && showOptions && (
          <motion.div
            className="options-menu"
            variants={optionsVariants}
            initial="closed"
            animate="open"
            exit="closed"
          >
            {!isPocSubmitterRoute && (
              <motion.div
                className="option-item"
                whileHover={{ scale: 1.05, x: -5 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleChatOptionClick}
              >
                <div className="option-icon chat-option">
                  <MessageOutlined />
                </div>
                <div className="option-content">
                  <div className="option-title">Chat with ESG Assistant</div>
                  <div className="option-description">
                    Get help with ESG topics
                  </div>
                </div>
              </motion.div>
            )}

            <motion.div
              className="option-item"
              whileHover={{ scale: 1.05, x: -5 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleReportOptionClick}
            >
              <div className="option-icon report-option">
                <BugOutlined />
              </div>
              <div className="option-content">
                <div className="option-title">Report an Issue</div>
                <div className="option-description">
                  Capture page & report problem
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Chat Window – completely disabled on /esg/poc/submitter */}
      <AnimatePresence>
        {isOpen && activeChat && !isPocSubmitterRoute && (
          <motion.div
            className="chatbot-window"
            variants={chatWindowVariants}
            initial="closed"
            animate="open"
            exit="closed"
          >
            {/* Header */}
            <motion.div
              className="chatbot-header"
              initial={{ y: -20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.1 }}
            >
              <div className="chatbot-title">
                <motion.div
                  className="bot-avatar"
                  animate={{ rotate: [0, 10, -10, 0] }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    repeatDelay: 5,
                  }}
                >
                  <RobotOutlined />
                </motion.div>
                <div className="title-content">
                  <div className="title-main">ESG Assistant</div>
                  <div className="title-sub">Online • Ready to help</div>
                </div>
              </div>
              <Button
                type="text"
                icon={<CloseOutlined />}
                onClick={() => {
                  setActiveChat(false);
                  setIsOpen(false);
                }}
                className="close-button"
              />
            </motion.div>

            {/* Messages */}
            <div
              className="messages-container"
              ref={messagesContainerRef}
              style={{ overflowY: "auto" }}
            >
              <AnimatePresence>
                {conversation.map((msg) => (
                  <motion.div
                    key={msg.id}
                    className={`message ${
                      msg.isUser ? "user-message" : "bot-message"
                    }`}
                    variants={messageVariants}
                    initial="initial"
                    animate="animate"
                    exit="exit"
                    transition={{ duration: 0.4, type: "spring" }}
                    layout
                  >
                    <div className="message-avatar">
                      {msg.isUser ? <UserOutlined /> : <RobotOutlined />}
                    </div>
                    <div className="message-content">
                      <div className="message-text">
                        {formatMessage(msg.text)}
                        {!msg.isUser && (
                          <div className="message-metadata">
                            {renderSources(msg.sources)}
                            {renderDisclaimer(msg.disclaimer)}
                          </div>
                        )}
                      </div>
                      <div className="message-time">
                        {msg.timestamp &&
                          msg.timestamp.toLocaleTimeString([], {
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>

              {(isTyping || isSending) && (
                <motion.div
                  className="message bot-message"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="message-avatar">
                    <RobotOutlined />
                  </div>
                  <div className="message-content">
                    <div className="message-text typing-indicator">
                      {isSending ? (
                        <motion.div
                          className="loading-dots"
                          variants={loadingDotsVariants}
                          initial="initial"
                          animate="animate"
                        >
                          <motion.span variants={dotVariants}>.</motion.span>
                          <motion.span variants={dotVariants}>.</motion.span>
                          <motion.span variants={dotVariants}>.</motion.span>
                        </motion.div>
                      ) : (
                        <div className="typing-content">
                          {formatMessage(typingText)}
                          <motion.span
                            className="typing-cursor"
                            animate={{ opacity: [1, 0] }}
                            transition={{
                              duration: 0.8,
                              repeat: Infinity,
                            }}
                          >
                            |
                          </motion.span>
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <motion.div
              className="input-container"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              <Input.TextArea
                ref={inputRef}
                rows={1}
                value={messageText}
                onChange={(e) => setMessageText(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Ask about ESG topics..."
                disabled={isSending || isTyping}
                className="message-input"
                autoSize={{ minRows: 1, maxRows: 4 }}
              />
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Button
                  type="primary"
                  icon={<SendOutlined />}
                  onClick={handleSendMessage}
                  loading={isSending}
                  disabled={!messageText.trim() || isSending || isTyping}
                  className="send-button"
                >
                  Send
                </Button>
              </motion.div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Report Issue Modal */}
      <Modal
        title={
          <div className="report-modal-title">
            <BugOutlined />
            Report an Issue
          </div>
        }
        open={reportModalVisible}
        onCancel={() => {
          setReportModalVisible(false);
          setPageSnapshot(null);
          setScreenshotFile(null);
        }}
        footer={null}
        width={600}
        className="report-modal"
      >
        <Form
          form={reportForm}
          layout="vertical"
          onFinish={handleReportIssue}
          className="report-form"
        >
          {pageSnapshot && (
            <div className="snapshot-preview">
              <div className="snapshot-header">
                <CameraOutlined />
                Page Snapshot Captured
              </div>
              <div className="snapshot-image">
                <img src={pageSnapshot} alt="Page snapshot" />
              </div>
              <div className="snapshot-note">
                This screenshot will be included with your report
              </div>
            </div>
          )}

          <Form.Item
            name="description"
            label="Description"
            rules={[{ required: true, message: "Please describe the issue" }]}
          >
            <TextArea
              rows={4}
              placeholder="Please describe the issue in detail..."
              maxLength={500}
              showCount
            />
          </Form.Item>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              size="large"
              block
              loading={isReportLoading}
              className="submit-report-button"
            >
              Submit Report
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* styles unchanged */}
      <style jsx>{`
        .chatbot-container {
          position: fixed;
          bottom: 25px;
          right: 25px;
          z-index: 1000;
        }
        .chatbot-icon {
          width: 65px;
          height: 65px;
          border-radius: 50%;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
          position: relative;
          z-index: 1001;
          border: 3px solid white;
        }
        .notification-dot {
          position: absolute;
          top: 5px;
          right: 5px;
          width: 12px;
          height: 12px;
          border-radius: 50%;
          background: #10b981;
          border: 2px solid white;
        }
        .options-menu {
          position: absolute;
          bottom: 85px;
          right: 0;
          width: 320px;
          background: white;
          border-radius: 20px;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
          padding: 20px;
          border: 1px solid rgba(255, 255, 255, 0.2);
          backdrop-filter: blur(10px);
        }
        .option-item {
          display: flex;
          align-items: center;
          padding: 16px;
          border-radius: 16px;
          cursor: pointer;
          transition: all 0.3s ease;
          margin-bottom: 12px;
          background: #f8fafc;
          border: 1px solid #e2e8f0;
        }
        .option-item:hover {
          background: white;
          border-color: #667eea;
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(102, 126, 234, 0.15);
        }
        .option-item:last-child {
          margin-bottom: 0;
        }
        .option-icon {
          width: 50px;
          height: 50px;
          border-radius: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-right: 16px;
          font-size: 20px;
          color: white;
        }
        .chat-option {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        }
        .report-option {
          background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
        }
        .option-content {
          flex: 1;
        }
        .option-title {
          font-weight: 600;
          color: #1e293b;
          font-size: 15px;
          margin-bottom: 4px;
        }
        .option-description {
          font-size: 13px;
          color: #64748b;
        }
        .chatbot-window {
          position: absolute;
          bottom: 85px;
          right: 0;
          width: 420px;
          height: 600px;
          background: white;
          border-radius: 24px;
          box-shadow: 0 25px 80px rgba(0, 0, 0, 0.15);
          display: flex;
          flex-direction: column;
          overflow: hidden;
          border: 1px solid rgba(255, 255, 255, 0.2);
          backdrop-filter: blur(10px);
        }
        .chatbot-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 24px;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
        }
        .chatbot-title {
          display: flex;
          align-items: center;
          gap: 12px;
        }
        .bot-avatar {
          width: 44px;
          height: 44px;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.2);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 18px;
          border: 2px solid rgba(255, 255, 255, 0.3);
        }
        .title-content {
          display: flex;
          flex-direction: column;
        }
        .title-main {
          font-weight: 700;
          color: white;
          font-size: 18px;
        }
        .title-sub {
          font-size: 12px;
          color: rgba(255, 255, 255, 0.8);
          margin-top: 2px;
        }
        .close-button {
          color: rgba(255, 255, 255, 0.8);
          border: none;
          width: 36px;
          height: 36px;
          border-radius: 10px;
          background: rgba(255, 255, 255, 0.1);
        }
        .close-button:hover {
          background: rgba(255, 255, 255, 0.2);
          color: white;
        }
        .messages-container {
          flex: 1;
          padding: 24px;
          overflow-y: auto;
          display: flex;
          flex-direction: column;
          gap: 20px;
          background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
        }
        .message {
          display: flex;
          gap: 12px;
          max-width: 85%;
        }
        .user-message {
          align-self: flex-end;
          flex-direction: row-reverse;
        }
        .bot-message {
          align-self: flex-start;
        }
        .message-avatar {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          background: white;
          border: 2px solid #e2e8f0;
          flex-shrink: 0;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
          font-size: 16px;
        }
        .user-message .message-avatar {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border-color: #667eea;
          color: white;
        }
        .bot-message .message-avatar {
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          border-color: #10b981;
          color: white;
        }
        .message-content {
          display: flex;
          flex-direction: column;
          gap: 6px;
          max-width: 100%;
        }
        .message-text {
          padding: 16px 20px;
          border-radius: 20px;
          font-size: 14px;
          line-height: 1.5;
          word-wrap: break-word;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
          position: relative;
        }
        .user-message .message-text {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          border-bottom-right-radius: 8px;
        }
        .bot-message .message-text {
          background: white;
          color: #334155;
          border-bottom-left-radius: 8px;
          border: 1px solid #e2e8f0;
        }
        .message-time {
          font-size: 11px;
          color: #94a3b8;
          padding: 0 8px;
          font-weight: 500;
        }
        .user-message .message-time {
          text-align: right;
        }
        .message-metadata {
          margin-top: 12px;
          padding-top: 12px;
          border-top: 1px solid rgba(0, 0, 0, 0.1);
        }
        .sources-section,
        .disclaimer-section {
          margin-top: 8px;
        }
        .sources-title,
        .disclaimer-title {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 12px;
          font-weight: 600;
          color: #64748b;
          margin-bottom: 6px;
        }
        .sources-list {
          display: flex;
          flex-wrap: wrap;
          gap: 4px;
        }
        .source-tag {
          font-size: 11px;
          padding: 2px 8px;
          border-radius: 12px;
        }
        .disclaimer-text {
          font-size: 12px;
          color: #64748b;
          font-style: italic;
          line-height: 1.4;
        }
        .typing-indicator {
          min-height: 20px;
          display: flex;
          align-items: center;
        }
        .loading-dots {
          display: flex;
          gap: 4px;
          color: #64748b;
          font-weight: bold;
          font-size: 16px;
        }
        .typing-content {
          position: relative;
        }
        .typing-cursor {
          color: #10b981;
          font-weight: bold;
          margin-left: 2px;
        }
        .input-container {
          padding: 20px;
          border-top: 1px solid #e2e8f0;
          display: flex;
          gap: 12px;
          align-items: flex-end;
          background: white;
        }
        .message-input {
          flex: 1;
          resize: none;
          border-radius: 16px;
          border: 2px solid #e2e8f0;
          padding: 16px 20px;
          font-size: 14px;
          transition: all 0.3s ease;
          background: #f8fafc;
        }
        .message-input:focus {
          border-color: #667eea;
          box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
          background: white;
        }
        .message-input:disabled {
          background: #f1f5f9;
          color: #94a3b8;
        }
        .send-button {
          height: 50px;
          padding: 0 24px;
          border-radius: 16px;
          font-weight: 600;
          border: none;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        }
        .send-button:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 8px 20px rgba(102, 126, 234, 0.3);
        }
        .send-button:disabled {
          background: #cbd5e1;
          color: #64748b;
        }
        .report-modal :global(.ant-modal-content) {
          border-radius: 20px;
          overflow: hidden;
        }
        .report-modal-title {
          display: flex;
          align-items: center;
          font-size: 20px;
          font-weight: 600;
          color: #1e293b;
          gap: 8px;
        }
        .snapshot-preview {
          margin-bottom: 24px;
          border: 1px solid #e2e8f0;
          border-radius: 16px;
          overflow: hidden;
          background: white;
        }
        .snapshot-header {
          padding: 16px 20px;
          background: #f8fafc;
          border-bottom: 1px solid #e2e8f0;
          font-weight: 600;
          color: #374151;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .snapshot-image {
          padding: 20px;
          background: white;
          text-align: center;
        }
        .snapshot-image img {
          max-width: 100%;
          max-height: 200px;
          border-radius: 8px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }
        .snapshot-note {
          padding: 12px 20px;
          background: #f8fafc;
          font-size: 13px;
          color: #64748b;
          text-align: center;
          border-top: 1px solid #e2e8f0;
        }
        .report-form :global(.ant-form-item-label > label) {
          color: #374151;
          fontweight: 600;
          font-size: 14px;
        }
        .report-form :global(.ant-input) {
          border-radius: 12px;
          border: 2px solid #e2e8f0;
          padding: 12px 16px;
          font-size: 14px;
        }
        .report-form :global(.ant-input:focus) {
          border-color: #667eea;
          box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
        }
        .submit-report-button {
          height: 50px;
          border-radius: 16px;
          font-weight: 600;
          border: none;
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
        }
        .submit-report-button:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 8px 20px rgba(16, 185, 129, 0.3);
        }
        .messages-container::-webkit-scrollbar {
          width: 6px;
        }
        .messages-container::-webkit-scrollbar-track {
          background: rgba(241, 245, 249, 0.5);
          border-radius: 3px;
        }
        .messages-container::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 3px;
        }
        .messages-container::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }
        @media (max-width: 480px) {
          .chatbot-container {
            bottom: 15px;
            right: 15px;
          }
          .options-menu {
            width: calc(100vw - 30px);
            right: 15px;
          }
          .chatbot-window {
            width: calc(100vw - 30px);
            height: calc(100vh - 100px);
            bottom: 75px;
            right: 15px;
            border-radius: 20px;
          }
          .chatbot-icon {
            width: 60px;
            height: 60px;
          }
        }
      `}</style>
    </div>
  );
};

export default Chatbot;
